import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, LogOut, Eye, Sparkles, GripVertical, Languages, Copy, RefreshCw, ExternalLink, FileText, Package, Menu as MenuIcon, LayoutTemplate, Settings, CreditCard, Plug, UserCog, ExternalLink as OpenIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cmsCall, ADMIN_TOKEN_KEY, adminToken, vertaalTeksten } from "@/lib/cms";
import { BLOCK_TYPES } from "@/components/Blocks";
import { ImageField } from "@/components/ImageField";
import { TextField } from "@/pages/beheer/TextField";
import { BlockEditor } from "@/pages/beheer/BlockEditor";
import { BLOCK_SCHEMAS, emptyBlock } from "@/pages/beheer/blockSchema";
import { Productpagina } from "@/pages/beheer/Productpagina";
import { Claude } from "@/pages/beheer/Claude";
import { MobielIndeling, VoorbeeldDialog, WeergaveTabs } from "@/pages/beheer/WeergaveIndeling";

const MENU_VELD_LABELS = {
  header_menus: "Menu's in de header (iedereen)",
  klant_menus: "Extra menu's voor ingelogde klanten",
  footer_menus: "Menu's in de footer",
};

const SECTIONS = [
  { id: "pages", label: "Pagina's", icon: FileText, groep: "Content" },
  { id: "productpagina", label: "Productpagina", icon: Package, groep: "Content" },
  { id: "menus", label: "Menu's", icon: MenuIcon, groep: "Content" },
  { id: "site", label: "Header & Footer", icon: LayoutTemplate, groep: "Content" },
  { id: "instellingen", label: "Instellingen", icon: Settings, groep: "Shop" },
  { id: "betalingen", label: "Betalingen", icon: CreditCard, groep: "Shop" },
  { id: "integratie", label: "Integratie", icon: Plug, groep: "Systeem" },
  { id: "claude", label: "Claude", icon: Sparkles, groep: "Systeem" },
  { id: "account", label: "Account", icon: UserCog, groep: "Systeem" },
];

/** Alle {nl,en,de,fr}-velden in een object verzamelen (recursief). */
const zoekTaalVelden = (node, pad = "", uit = []) => {
  if (!node || typeof node !== "object") return uit;
  if (Array.isArray(node)) {
    node.forEach((v, i) => zoekTaalVelden(v, `${pad}[${i}]`, uit));
    return uit;
  }
  if (typeof node.nl === "string") {
    uit.push({ pad, node });
    return uit;
  }
  Object.entries(node).forEach(([k, v]) => zoekTaalVelden(v, pad ? `${pad}.${k}` : k, uit));
  return uit;
};

/** Vertaalt elk NL-veld in een object en geeft een nieuwe kopie terug. */
const vertaalObject = async (obj, { alleenLeeg = false } = {}) => {
  const velden = zoekTaalVelden(obj).filter(({ node }) => (node.nl || "").trim());
  const teVertalen = alleenLeeg
    ? velden.filter(({ node }) => !node.en?.trim() || !node.de?.trim() || !node.fr?.trim())
    : velden;
  if (teVertalen.length === 0) return { kopie: obj, aantal: 0 };

  const items = teVertalen.map(({ pad, node }) => ({ sleutel: pad || "root", nl: node.nl }));
  const vertalingen = await vertaalTeksten(items);
  const kopie = JSON.parse(JSON.stringify(obj));
  const kopieVelden = zoekTaalVelden(kopie);
  const perPad = new Map(kopieVelden.map(({ pad, node }) => [pad || "root", node]));
  let aantal = 0;
  Object.entries(vertalingen).forEach(([pad, v]) => {
    const doel = perPad.get(pad);
    if (!doel) return;
    ["en", "de", "fr"].forEach((l) => { if (v[l]) doel[l] = v[l]; });
    aantal += 1;
  });
  return { kopie, aantal };
};


const Pages = () => {
  const [pages, setPages] = useState([]);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [vertaalt, setVertaalt] = useState(false);
  const [weergave, setWeergave] = useState("desktop");
  const [voorbeeld, setVoorbeeld] = useState(false);
  const load = useCallback(() => cmsCall("GET", "/beheer/pages", null, { admin: true }).then(setPages).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await cmsCall("PUT", `/beheer/pages/${current.slug}`, current, { admin: true });
      toast.success("Pagina opgeslagen");
      load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const create = async () => {
    const slug = window.prompt("Slug van de nieuwe pagina (bijv. over-ons)");
    if (!slug) return;
    try {
      const p = await cmsCall("POST", "/beheer/pages", { slug, titel: { nl: slug }, gepubliceerd: false, blokken: [] }, { admin: true });
      toast.success("Pagina aangemaakt");
      load(); setCurrent(p);
    } catch (e) { toast.error(e.message); }
  };

  const vertaalPagina = async (alleenLeeg) => {
    if (!current || vertaalt) return;
    setVertaalt(true);
    try {
      const { kopie, aantal } = await vertaalObject(current, { alleenLeeg });
      if (aantal === 0) {
        toast.info(alleenLeeg ? "Alle vertalingen zijn al gevuld" : "Geen Nederlandse tekst gevonden");
      } else {
        setCurrent(kopie);
        toast.success(`${aantal} veld(en) vertaald — controleer en klik op Opslaan`);
      }
    } catch (e) {
      toast.error(e.message || "Vertalen mislukt");
    } finally {
      setVertaalt(false);
    }
  };

  const move = (i, dir) => {
    const blokken = [...current.blokken];
    const j = i + dir;
    if (j < 0 || j >= blokken.length) return;
    [blokken[i], blokken[j]] = [blokken[j], blokken[i]];
    setCurrent({ ...current, blokken });
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card className="h-fit p-4 lg:col-span-1">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Pagina's</h2>
          <Button size="sm" onClick={create} data-testid="page-create"><Plus className="mr-1 h-4 w-4" />Nieuw</Button>
        </div>
        <div className="mt-3 space-y-1">
          {pages.map((p) => (
            <button key={p.slug} onClick={() => setCurrent(p)} className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm ${current?.slug === p.slug ? "bg-secondary font-medium" : "hover:bg-secondary"}`} data-testid={`page-item-${p.slug}`}>
              <span>{p.titel?.nl || p.slug}</span>
              <span className="text-xs text-muted-foreground">{p.gepubliceerd ? "live" : "concept"}</span>
            </button>
          ))}
        </div>
      </Card>

      <div className="lg:col-span-2">
        {!current ? (
          <Card className="p-6 text-sm text-muted-foreground">Kies links een pagina om te bewerken.</Card>
        ) : (
          <Card className="space-y-5 p-5" data-testid="page-editor">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
                <span className="font-mono text-sm text-muted-foreground">/{current.slug === "home" ? "" : current.slug}</span>
                <a href={current.slug === "home" ? "/" : `/${current.slug}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary" aria-label="Pagina bekijken" data-testid="page-open"><OpenIcon className="h-4 w-4" /></a>
              </h2>
              <div className="flex flex-wrap items-center gap-3">
                <WeergaveTabs value={weergave} onChange={setWeergave} testIdPrefix="page-weergave" />
                <Button variant="outline" size="sm" onClick={() => setVoorbeeld(true)} data-testid="page-preview">
                  <Eye className="mr-2 h-4 w-4" />Voorbeeld
                </Button>
                <Button variant="outline" size="sm" onClick={() => vertaalPagina(true)} disabled={vertaalt} data-testid="page-translate-empty">
                  {vertaalt ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Languages className="mr-2 h-4 w-4" />}
                  Ontbrekende vertalingen
                </Button>
                <Button variant="outline" size="sm" onClick={() => vertaalPagina(false)} disabled={vertaalt} data-testid="page-translate-all">
                  {vertaalt ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Languages className="mr-2 h-4 w-4" />}
                  Hele pagina vertalen
                </Button>
                <Label className="flex items-center gap-2 text-sm">
                  <Switch checked={!!current.gepubliceerd} onCheckedChange={(v) => setCurrent({ ...current, gepubliceerd: v })} data-testid="page-published-switch" />
                  Gepubliceerd
                </Label>
                <Button onClick={save} disabled={saving} data-testid="page-save">
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Opslaan
                </Button>
              </div>
            </div>
            <TextField label="Paginatitel" value={current.titel} onChange={(v) => setCurrent({ ...current, titel: v })} />
            <Separator />
            {weergave === "mobiel" && (
              <MobielIndeling
                blokken={current.blokken || []}
                mobiel={current.mobiel || null}
                labelVan={(b) => b.titel?.nl || BLOCK_SCHEMAS[b.type]?.label || b.type}
                onChange={(mobiel) => setCurrent({ ...current, mobiel })}
                testIdPrefix="page-mobiel"
              />
            )}
            {weergave === "desktop" && (current.blokken || []).map((b, i) => (
              <BlockEditor
                key={b.id || i}
                block={b}
                onChange={(nb) => setCurrent({ ...current, blokken: current.blokken.map((x, j) => (j === i ? nb : x)) })}
                onRemove={() => setCurrent({ ...current, blokken: current.blokken.filter((_, j) => j !== i) })}
                onUp={() => move(i, -1)}
                onDown={() => move(i, 1)}
              />
            ))}
            <VoorbeeldDialog
              open={voorbeeld}
              onOpenChange={setVoorbeeld}
              pad={current.gepubliceerd ? (current.slug === "home" ? "/" : `/${current.slug}`) : null}
              titel={`Voorbeeld /${current.slug === "home" ? "" : current.slug}`}
              melding={current.gepubliceerd ? "Toont de opgeslagen, gepubliceerde versie van de pagina." : "Publiceer en sla de pagina eerst op om een voorbeeld te zien."}
            />
            {weergave === "desktop" && <div className="rounded-xl border border-dashed p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Blok toevoegen</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {BLOCK_TYPES.map((type) => (
                  <Button key={type} variant="outline" size="sm" className="rounded-full" onClick={() => setCurrent({ ...current, blokken: [...(current.blokken || []), emptyBlock(type)] })} data-testid={`add-block-${type}`}>
                    <Plus className="mr-1 h-3.5 w-3.5" />{BLOCK_SCHEMAS[type]?.label || type}
                  </Button>
                ))}
              </div>
            </div>}
            {!current.systeem && (
              <Button variant="ghost" onClick={async () => {
                if (!window.confirm("Pagina verwijderen?")) return;
                await cmsCall("DELETE", `/beheer/pages/${current.slug}`, null, { admin: true });
                toast.success("Pagina verwijderd"); setCurrent(null); load();
              }} data-testid="page-delete"><Trash2 className="mr-2 h-4 w-4" />Verwijderen</Button>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

const Menus = () => {
  const [menus, setMenus] = useState([]);
  const [drag, setDrag] = useState(null);
  const load = useCallback(() => cmsCall("GET", "/beheer/menus", null, { admin: true }).then(setMenus).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (menu) => {
    try { await cmsCall("PUT", `/beheer/menus/${menu.id}`, menu, { admin: true }); toast.success("Menu opgeslagen"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const create = async () => {
    const id = window.prompt("ID van het nieuwe menu (bijv. servicemenu)");
    if (!id) return;
    try { await cmsCall("POST", "/beheer/menus", { id, naam: id, items: [] }, { admin: true }); toast.success("Menu aangemaakt"); load(); }
    catch (e) { toast.error(e.message); }
  };
  const setMenu = (mi, menu) => setMenus(menus.map((m, i) => (i === mi ? menu : m)));

  return (
    <div className="space-y-6">
      <Button size="sm" onClick={create} data-testid="menu-create"><Plus className="mr-1 h-4 w-4" />Nieuw menu</Button>
      {menus.map((menu, mi) => (
        <Card key={menu.id} className="space-y-4 p-5" data-testid={`menu-${menu.id}`}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-semibold">{menu.naam}</h2>
              <p className="text-xs text-muted-foreground">id: {menu.id} · sleep items om te sorteren</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => save(menu)} data-testid={`menu-save-${menu.id}`}>Opslaan</Button>
              <Button size="sm" variant="ghost" onClick={async () => { if (!window.confirm("Menu verwijderen?")) return; await cmsCall("DELETE", `/beheer/menus/${menu.id}`, null, { admin: true }); load(); }}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="space-y-3">
            {(menu.items || []).map((item, ii) => (
              <div
                key={ii}
                draggable
                onDragStart={() => setDrag({ mi, ii })}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (!drag || drag.mi !== mi) return;
                  const items = [...menu.items];
                  const [moved] = items.splice(drag.ii, 1);
                  items.splice(ii, 0, moved);
                  setMenu(mi, { ...menu, items });
                  setDrag(null);
                }}
                className="flex cursor-move gap-3 rounded-md border bg-card p-3"
                data-testid="menu-item"
              >
                <GripVertical className="mt-2 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="flex-1 space-y-3">
                  <TextField label={`Item ${ii + 1} label`} value={item.label} onChange={(v) => setMenu(mi, { ...menu, items: menu.items.map((x, j) => (j === ii ? { ...x, label: v } : x)) })} />
                  <div className="grid gap-2 sm:grid-cols-2">
                    <select
                      className="h-9 rounded-md border bg-background px-2 text-sm"
                      value={item.soort || "route"}
                      onChange={(e) => setMenu(mi, { ...menu, items: menu.items.map((x, j) => (j === ii ? { ...x, soort: e.target.value } : x)) })}
                      data-testid="menu-item-type"
                    >
                      <option value="pagina">CMS-pagina (slug)</option>
                      <option value="route">App-route (/login)</option>
                      <option value="extern">Externe URL</option>
                    </select>
                    <Input
                      value={item.waarde || ""}
                      onChange={(e) => setMenu(mi, { ...menu, items: menu.items.map((x, j) => (j === ii ? { ...x, waarde: e.target.value } : x)) })}
                      placeholder="home / /login / https://…"
                      data-testid="menu-item-value"
                    />
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setMenu(mi, { ...menu, items: menu.items.filter((_, j) => j !== ii) })}>
                    <Trash2 className="mr-2 h-4 w-4" />Item verwijderen
                  </Button>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setMenu(mi, { ...menu, items: [...(menu.items || []), { label: { nl: "" }, soort: "route", waarde: "/" }] })} data-testid={`menu-add-item-${menu.id}`}>
              <Plus className="mr-1 h-4 w-4" />Item toevoegen
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
};

const SiteSettings = () => {
  const [site, setSite] = useState(null);
  const [menus, setMenus] = useState([]);
  useEffect(() => {
    cmsCall("GET", "/beheer/site", null, { admin: true }).then(setSite).catch((e) => toast.error(e.message));
    cmsCall("GET", "/beheer/menus", null, { admin: true }).then(setMenus).catch(() => {});
  }, []);
  if (!site) return <Card className="p-6 text-sm text-muted-foreground">Laden…</Card>;

  const toggle = (field, id) => {
    const list = site[field] || [];
    setSite({ ...site, [field]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] });
  };

  return (
    <Card className="space-y-5 p-5" data-testid="site-editor">
      <ImageField
        label="Logo"
        value={site.logo_url}
        onChange={(url) => setSite({ ...site, logo_url: url })}
        testId="site-logo"
      />
      {["header_menus", "klant_menus", "footer_menus"].map((field) => (
        <div key={field}>
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            {MENU_VELD_LABELS[field]}
          </Label>
          <div className="mt-2 flex flex-wrap gap-3">
            {menus.map((m) => (
              <Label key={m.id} className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                <Switch checked={(site[field] || []).includes(m.id)} onCheckedChange={() => toggle(field, m.id)} data-testid={`${field}-${m.id}`} />
                {m.naam}
              </Label>
            ))}
          </div>
        </div>
      ))}
      <TextField label="Footer-tekst" value={site.footer_tekst} onChange={(v) => setSite({ ...site, footer_tekst: v })} rows={2} />
      <Separator />
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">Footer-tekstkolommen</Label>
      {(site.footer_kolommen || []).map((k, i) => (
        <Card key={i} className="space-y-3 p-3">
          <TextField label={`Kolom ${i + 1} titel`} value={k.titel} onChange={(v) => setSite({ ...site, footer_kolommen: site.footer_kolommen.map((x, j) => (j === i ? { ...x, titel: v } : x)) })} />
          <TextField label={`Kolom ${i + 1} tekst`} value={k.tekst} onChange={(v) => setSite({ ...site, footer_kolommen: site.footer_kolommen.map((x, j) => (j === i ? { ...x, tekst: v } : x)) })} rows={2} />
          <Button variant="ghost" size="sm" onClick={() => setSite({ ...site, footer_kolommen: site.footer_kolommen.filter((_, j) => j !== i) })}>
            <Trash2 className="mr-2 h-4 w-4" />Kolom verwijderen
          </Button>
        </Card>
      ))}
      <Button variant="outline" size="sm" onClick={() => setSite({ ...site, footer_kolommen: [...(site.footer_kolommen || []), { titel: {}, tekst: {} }] })} data-testid="footer-add-column">
        <Plus className="mr-1 h-4 w-4" />Kolom toevoegen
      </Button>
      <Separator />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {["email", "telefoon", "whatsapp", "adres"].map((f) => (
          <div key={f} className="space-y-2">
            <Label className="capitalize">{f === "whatsapp" ? "WhatsApp-nummer (leeg = telefoon)" : f}</Label>
            <Input value={site.contact?.[f] || ""} onChange={(e) => setSite({ ...site, contact: { ...(site.contact || {}), [f]: e.target.value } })} data-testid={`contact-${f}`} />
          </div>
        ))}
      </div>
      <Button onClick={async () => {
        try { await cmsCall("PUT", "/beheer/site", site, { admin: true }); toast.success("Header & footer opgeslagen"); }
        catch (e) { toast.error(e.message); }
      }} data-testid="site-save">Opslaan</Button>
    </Card>
  );
};

const LANDEN_BEHEER = [["nl", "Nederland"], ["be", "België"], ["de", "Duitsland"], ["fr", "Frankrijk"]];

const Instellingen = () => {  const [s, setS] = useState(null);
  const [key, setKey] = useState("");
  useEffect(() => { cmsCall("GET", "/beheer/instellingen", null, { admin: true }).then(setS).catch((e) => toast.error(e.message)); }, []);
  if (!s) return <Card className="p-6 text-sm text-muted-foreground">Laden…</Card>;
  return (
    <Card className="max-w-xl space-y-5 p-5" data-testid="instellingen-editor">
      <Label className="flex items-center justify-between gap-4">
        <span>Direct betalen (Mollie)</span>
        <Switch checked={!!s.direct_betalen} onCheckedChange={(v) => setS({ ...s, direct_betalen: v })} data-testid="setting-direct-betalen" />
      </Label>
      <div className="space-y-2">
        <Label>Kortingspercentage bij direct betalen</Label>
        <Input type="number" step="0.5" min="0" value={s.korting_percentage} onChange={(e) => setS({ ...s, korting_percentage: e.target.value })} data-testid="setting-korting" />
      </div>
      <div className="space-y-2">
        <Label>Mollie API-key</Label>
        <Input type="password" placeholder={s.mollie_api_key_masked || "test_… of live_…"} value={key} onChange={(e) => setKey(e.target.value)} data-testid="setting-mollie-key" />
        <p className="text-xs text-muted-foreground">
          {s.mollie_geconfigureerd ? `Ingesteld: ${s.mollie_api_key_masked}` : "Nog niet ingesteld"} · leeg laten = ongewijzigd
        </p>
      </div>
      <div className="space-y-2">
        <Label>Minimum bestelbedrag per land (excl. BTW)</Label>
        <p className="text-xs text-muted-foreground">
          Tijdelijke terugval. Zodra het CRM dit levert (Menu → Verzenden), is het CRM leidend. 0 = geen minimum.
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {LANDEN_BEHEER.map(([code, naam]) => (
            <div key={code} className="space-y-1">
              <Label htmlFor={`min-${code}`} className="text-xs text-muted-foreground">{naam}</Label>
              <Input
                id={`min-${code}`}
                type="number"
                min="0"
                step="5"
                value={(s.minimum_bestelbedrag || {})[code] ?? 0}
                onChange={(e) => setS((prev) => ({ ...prev, minimum_bestelbedrag: { ...(prev.minimum_bestelbedrag || {}), [code]: e.target.value } }))}
                data-testid={`setting-minimum-${code}`}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label>Verzendkosten per land (excl. BTW)</Label>
        <p className="text-xs text-muted-foreground">
          Tijdelijke terugval; het CRM is leidend zodra `verzendkosten` geleverd wordt. 0 = niet tonen in het mandje.
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {LANDEN_BEHEER.map(([code, naam]) => (
            <div key={code} className="space-y-1">
              <Label htmlFor={`verzend-${code}`} className="text-xs text-muted-foreground">{naam}</Label>
              <Input
                id={`verzend-${code}`}
                type="number"
                min="0"
                step="0.5"
                value={(s.verzendkosten || {})[code] ?? 0}
                onChange={(e) => setS((prev) => ({ ...prev, verzendkosten: { ...(prev.verzendkosten || {}), [code]: e.target.value } }))}
                data-testid={`setting-verzendkosten-${code}`}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label>Gratis verzending vanaf (excl. BTW)</Label>
        <p className="text-xs text-muted-foreground">0 = geen gratis-verzendingsdrempel.</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {LANDEN_BEHEER.map(([code, naam]) => (
            <div key={code} className="space-y-1">
              <Label htmlFor={`gratis-${code}`} className="text-xs text-muted-foreground">{naam}</Label>
              <Input
                id={`gratis-${code}`}
                type="number"
                min="0"
                step="5"
                value={(s.gratis_verzending_vanaf || {})[code] ?? 0}
                onChange={(e) => setS((prev) => ({ ...prev, gratis_verzending_vanaf: { ...(prev.gratis_verzending_vanaf || {}), [code]: e.target.value } }))}
                data-testid={`setting-gratis-vanaf-${code}`}
              />
            </div>
          ))}
        </div>
      </div>
      <Button onClick={async () => {
        try {
          const perLand = (veld) => {
            const uit = {};
            LANDEN_BEHEER.forEach(([c]) => { uit[c] = Number((s[veld] || {})[c]) || 0; });
            return uit;
          };
          const res = await cmsCall("PUT", "/beheer/instellingen", {
            direct_betalen: !!s.direct_betalen,
            korting_percentage: Number(s.korting_percentage) || 0,
            mollie_api_key: key || null,
            minimum_bestelbedrag: perLand("minimum_bestelbedrag"),
            verzendkosten: perLand("verzendkosten"),
            gratis_verzending_vanaf: perLand("gratis_verzending_vanaf"),
          }, { admin: true });
          setS(res); setKey(""); toast.success("Instellingen opgeslagen");
        } catch (e) { toast.error(e.message); }
      }} data-testid="instellingen-save">Opslaan</Button>
    </Card>
  );
};

const Account = ({ email }) => {
  const [huidig, setHuidig] = useState("");
  const [nieuw, setNieuw] = useState("");
  const [herhaal, setHerhaal] = useState("");
  const [busy, setBusy] = useState(false);
  const [fout, setFout] = useState("");

  const tekortLang = nieuw.length > 0 && nieuw.length < 8;
  const ongelijk = herhaal.length > 0 && nieuw !== herhaal;
  const kanOpslaan = huidig && nieuw.length >= 8 && nieuw === herhaal && !busy;

  const opslaan = async () => {
    if (!kanOpslaan) return;
    setFout("");
    setBusy(true);
    try {
      await cmsCall("POST", "/beheer/wachtwoord", { huidig_wachtwoord: huidig, nieuw_wachtwoord: nieuw }, { admin: true });
      setHuidig(""); setNieuw(""); setHerhaal("");
      toast.success("Wachtwoord gewijzigd. Andere apparaten zijn uitgelogd.");
    } catch (e) {
      setFout(e.message || "Wijzigen mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="max-w-xl p-5" data-testid="account-editor">
      <h2 className="font-display text-lg font-semibold">Wachtwoord wijzigen</h2>
      <p className="mt-1 text-sm text-muted-foreground" data-testid="account-email">
        Beheerder: {email || "—"}
      </p>
      <Separator className="my-5" />
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); opslaan(); }}>
        {fout && <Alert variant="destructive" data-testid="account-error"><AlertDescription>{fout}</AlertDescription></Alert>}
        <div className="space-y-2">
          <Label htmlFor="pw-huidig">Huidig wachtwoord</Label>
          <Input id="pw-huidig" type="password" autoComplete="current-password" value={huidig} onChange={(e) => setHuidig(e.target.value)} data-testid="account-huidig" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pw-nieuw">Nieuw wachtwoord</Label>
          <Input id="pw-nieuw" type="password" autoComplete="new-password" value={nieuw} onChange={(e) => setNieuw(e.target.value)} aria-invalid={tekortLang} data-testid="account-nieuw" />
          <p className={`text-xs ${tekortLang ? "text-destructive" : "text-muted-foreground"}`}>Minimaal 8 tekens.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="pw-herhaal">Nieuw wachtwoord herhalen</Label>
          <Input id="pw-herhaal" type="password" autoComplete="new-password" value={herhaal} onChange={(e) => setHerhaal(e.target.value)} aria-invalid={ongelijk} data-testid="account-herhaal" />
          {ongelijk && <p className="text-xs text-destructive" data-testid="account-mismatch">De wachtwoorden zijn niet gelijk.</p>}
        </div>
        <Button type="submit" disabled={!kanOpslaan} data-testid="account-save">
          {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Bezig…</> : "Wachtwoord wijzigen"}
        </Button>
      </form>
    </Card>
  );
};

const Betalingen = () => {
  const [data, setData] = useState(null);
  useEffect(() => { cmsCall("GET", "/beheer/betalingen", null, { admin: true }).then(setData).catch((e) => toast.error(e.message)); }, []);
  if (!data) return <Card className="p-6 text-sm text-muted-foreground">Laden…</Card>;
  return (
    <div className="space-y-6">
      <Card className="p-5" data-testid="betalingen-openstaand">
        <h2 className="font-display text-lg font-semibold">Betaald zonder CRM-order</h2>
        {data.openstaand.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">Geen openstaande gevallen.</p>
        ) : (
          <ul className="mt-3 space-y-2 text-sm">
            {data.openstaand.map((b) => (
              <li key={b.reference} className="rounded-md border bg-secondary p-3">
                {b.reference} · € {Number(b.totaal_incl_btw).toFixed(2)} · {b.status}
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Card className="p-5">
        <h2 className="font-display text-lg font-semibold">Alle betalingen</h2>
        <ul className="mt-3 space-y-2 text-sm">
          {data.betalingen.length === 0 && <li className="text-muted-foreground">Nog geen betalingen.</li>}
          {data.betalingen.map((b) => (
            <li key={b.reference} className="flex flex-wrap justify-between gap-2 border-b py-2">
              <span className="font-medium">{b.reference}</span>
              <span className="text-muted-foreground">{b.status} · € {Number(b.totaal_incl_btw).toFixed(2)} · {b.crm_order_id || "geen order"}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
};

const LinkRegel = ({ label, url, testid, uitleg }) => (
  <div className="rounded-md border bg-secondary p-3">
    <p className="text-sm font-medium">{label}</p>
    {uitleg && <p className="mt-0.5 text-xs text-muted-foreground">{uitleg}</p>}
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <code className="flex-1 break-all rounded bg-background px-2 py-1 font-mono text-xs" data-testid={`${testid}-url`}>{url}</code>
      <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link gekopieerd"); }} data-testid={`${testid}-copy`}>
        <Copy className="mr-2 h-3.5 w-3.5" />Kopiëren
      </Button>
      <Button size="sm" variant="ghost" asChild data-testid={`${testid}-open`}>
        <a href={url} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5" /></a>
      </Button>
    </div>
  </div>
);

const CHECK_STATUS = {
  ok: { label: "geleverd", klasse: "bg-emerald-100 text-emerald-800" },
  deels: { label: "deels geleverd", klasse: "bg-amber-100 text-amber-900" },
  ontbreekt: { label: "ontbreekt", klasse: "bg-rose-100 text-rose-800" },
  onbekend: { label: "niet te controleren", klasse: "bg-secondary text-muted-foreground" },
  fout: { label: "fout", klasse: "bg-rose-100 text-rose-800" },
};

const VERSCHIL_LABEL = {
  nieuw_geleverd: "Nieuw geleverd",
  weggevallen: "Weggevallen",
  endpoint: "Endpoint gewijzigd",
};

const SchapVerversen = () => {
  const [busy, setBusy] = useState(false);
  const verversen = async () => {
    setBusy(true);
    try {
      const res = await cmsCall("POST", "/beheer/schap-verversen", null, { admin: true });
      const bron = res.selectie === "crm" ? "CRM-selectie" : "selectie uit het beheer";
      toast.success(`Schap verversd: ${res.aantal} producten (${bron})`);
      if (res.melding) toast.info(res.melding, { duration: 10000 });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="outline" onClick={verversen} disabled={busy} data-testid="schap-verversen">
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
      Schap verversen
    </Button>
  );
};

const Aansluitcheck = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const laad = useCallback(() => {
    cmsCall("GET", "/beheer/aansluitcheck", null, { admin: true }).then(setData).catch((e) => toast.error(e.message));
  }, []);
  useEffect(laad, [laad]);

  const nuControleren = async () => {
    setBusy(true);
    try {
      const res = await cmsCall("POST", "/beheer/aansluitcheck", null, { admin: true });
      setData((d) => ({ ...(d || {}), laatste: res.laatste }));
      toast.success("Controle uitgevoerd");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const laatste = data?.laatste;
  return (
    <Card className="p-5" data-testid="beheer-aansluitcheck">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold">Automatische aansluitcheck</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Elke 24 uur logt de webshop met het CRM-testaccount in en controleert welke gevraagde velden en
            endpoints echt geleverd worden.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <SchapVerversen />
          <Button variant="outline" onClick={nuControleren} disabled={busy} data-testid="aansluitcheck-nu">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Nu controleren
          </Button>
        </div>
      </div>

      {!laatste && <p className="mt-4 text-sm text-muted-foreground" data-testid="aansluitcheck-leeg">Nog geen controle uitgevoerd.</p>}

      {laatste && (
        <div className="mt-5 space-y-4">
          <p className="text-xs text-muted-foreground" data-testid="aansluitcheck-tijd">
            Laatste controle: {new Date(laatste.uitgevoerd_op).toLocaleString("nl-NL")}
          </p>
          {!laatste.ok && (
            <Alert data-testid="aansluitcheck-melding">
              <AlertDescription>{laatste.melding}</AlertDescription>
            </Alert>
          )}

          {laatste.verschillen?.length > 0 && (
            <Alert className="border-amber-300 bg-amber-50" data-testid="aansluitcheck-verschillen">
              <AlertDescription>
                <span className="font-medium">Verschil met de vorige controle:</span>
                <ul className="mt-2 space-y-1 text-sm">
                  {laatste.verschillen.map((v) => (
                    <li key={`${v.soort}-${v.naam}`}>
                      {VERSCHIL_LABEL[v.soort] || v.soort}: <code className="font-mono text-xs">{v.naam}</code>{" "}
                      ({v.van} → {v.naar})
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {laatste.onderdelen?.map((o) => {
            const st = CHECK_STATUS[o.status] || CHECK_STATUS.onbekend;
            const ontbrekend = (o.velden || []).filter((v) => !v.aanwezig);
            return (
              <div key={o.aansluiting_id} className="rounded-md border p-3" data-testid={`aansluitcheck-${o.aansluiting_id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <code className="font-mono text-xs">{o.methode} {o.pad}</code>
                  <span className={`rounded px-2 py-0.5 text-xs font-medium ${st.klasse}`}>{st.label}</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{o.toelichting}</p>
                {ontbrekend.length > 0 && (
                  <p className="mt-2 text-xs">
                    Ontbreekt nog:{" "}
                    {ontbrekend.map((v) => (
                      <code key={v.naam} className="mr-1 font-mono">{v.naam}{v.verplicht ? "*" : ""}</code>
                    ))}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

const Integratie = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const laad = useCallback(() => {
    cmsCall("GET", "/beheer/integratie", null, { admin: true }).then(setData).catch((e) => toast.error(e.message));
  }, []);
  useEffect(laad, [laad]);

  const nieuweLink = async () => {
    setBusy(true);
    try {
      const res = await cmsCall("POST", "/beheer/integratie/link", null, { admin: true });
      setData((d) => ({ ...(d || {}), link: res.link }));
      toast.success("Nieuwe deellink aangemaakt. De oude link werkt niet meer.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const intrekken = async () => {
    setBusy(true);
    try {
      await cmsCall("DELETE", "/beheer/integratie/link", null, { admin: true });
      setData((d) => ({ ...(d || {}), link: null }));
      toast.success("Deellink ingetrokken");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <Card className="p-6 text-sm text-muted-foreground">Laden…</Card>;

  return (
    <div className="space-y-6" data-testid="beheer-integratie">
      <Card className="p-5">
        <h2 className="font-display text-lg font-semibold">Deellink voor het CRM</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Deel deze read-only link met de CRM-ontwikkelaars. Daarin staan alle {data.aantal} aansluitingen die de
          webshop bij het CRM opvraagt (versie {data.versie}). Er is geen inlog nodig; wie de link heeft, kan meekijken.
        </p>

        {data.link ? (
          <div className="mt-5 space-y-3">
            <LinkRegel
              label="Leespagina (voor mensen)"
              uitleg="Nette pagina met alle velden, foutcodes en de testchecklist."
              url={data.link.pagina_url}
              testid="integratie-pagina"
            />
            <LinkRegel
              label="Aansluitingen ophalen (voor het CRM)"
              uitleg="Alleen JSON met de aansluitingen — het CRM ziet hiermee direct wat het kan aanleveren."
              url={data.link.aansluitingen_url}
              testid="integratie-aansluitingen"
            />
            <LinkRegel
              label="Volledige specificatie (JSON)"
              uitleg="Inclusief afspraken en testchecklist."
              url={data.link.spec_url}
              testid="integratie-spec"
            />
            <p className="text-xs text-muted-foreground" data-testid="integratie-meta">
              Aangemaakt door {data.link.aangemaakt_door} op {new Date(data.link.aangemaakt_op).toLocaleString("nl-NL")}
            </p>
            <Separator />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={nieuweLink} disabled={busy} data-testid="integratie-vernieuw">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Nieuwe link (oude vervalt)
              </Button>
              <Button variant="ghost" onClick={intrekken} disabled={busy} data-testid="integratie-intrekken">
                <Trash2 className="mr-2 h-4 w-4" />Link intrekken
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-5">
            <Alert>
              <AlertDescription>Er is nog geen deellink. Maak er één aan om de specificatie te delen.</AlertDescription>
            </Alert>
            <Button className="mt-4" onClick={nieuweLink} disabled={busy} data-testid="integratie-maak">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Deellink aanmaken
            </Button>
          </div>
        )}
      </Card>
      <Aansluitcheck />
    </div>
  );
};

const Beheer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loggedIn, setLoggedIn] = useState(false);
  const [checking, setChecking] = useState(true);
  const [adminEmail, setAdminEmail] = useState("");
  const [section, setSection] = useState("pages");

  useEffect(() => {
    if (!adminToken()) { setChecking(false); return; }
    cmsCall("GET", "/beheer/me", null, { admin: true })
      .then((res) => { setAdminEmail(res?.admin?.email || ""); setLoggedIn(true); })
      .catch(() => window.localStorage.removeItem(ADMIN_TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  if (checking) return <div className="p-10 text-sm text-muted-foreground">{t("common.loading")}</div>;

  // Inloggen gebeurt op de aparte medewerkerspagina /stafflogin.
  if (!loggedIn) return <Navigate to="/stafflogin" replace />;

  const uitloggen = async () => {
    try { await cmsCall("POST", "/beheer/logout", null, { admin: true }); } catch (e) { /* ignore */ }
    window.localStorage.removeItem(ADMIN_TOKEN_KEY);
    setLoggedIn(false);
    navigate("/stafflogin", { replace: true });
  };

  const actief = SECTIONS.find((x) => x.id === section);
  const groepen = [...new Set(SECTIONS.map((x) => x.groep))];
  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-[240px_1fr]" data-testid="beheer-page">
      <aside className="border-b bg-ink text-ink-foreground lg:sticky lg:top-0 lg:h-screen lg:border-b-0 lg:border-r lg:border-white/10">
        <div className="flex items-center justify-between px-5 py-5 lg:block">
          <div>
            <p className="font-display text-lg font-bold tracking-tight">NextProduct</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-foreground/50">Beheer</p>
          </div>
          <a href="/" target="_blank" rel="noreferrer" className="text-xs text-ink-foreground/60 hover:text-ink-foreground lg:mt-3 lg:inline-flex lg:items-center lg:gap-1" data-testid="beheer-open-site">Site bekijken <ExternalLink className="ml-1 inline h-3 w-3" /></a>
        </div>
        <nav className="scrollbar-none flex gap-1 overflow-x-auto px-3 pb-3 lg:block lg:space-y-5 lg:px-3 lg:pb-0">
          {groepen.map((g) => (
            <div key={g} className="flex gap-1 lg:block lg:space-y-0.5">
              <p className="hidden px-3 pb-1 font-mono text-[10px] uppercase tracking-widest text-ink-foreground/40 lg:block">{g}</p>
              {SECTIONS.filter((x) => x.groep === g).map((x) => {
                const Icon = x.icon;
                const aan = section === x.id;
                return (
                  <button key={x.id} onClick={() => setSection(x.id)} className={`flex shrink-0 items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors ${aan ? "bg-ink-foreground text-ink font-semibold" : "text-ink-foreground/70 hover:bg-white/10 hover:text-ink-foreground"}`} data-testid={`beheer-nav-${x.id}`}>
                    <Icon className="h-4 w-4" aria-hidden="true" />{x.label}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="hidden px-3 lg:absolute lg:bottom-4 lg:left-0 lg:right-0 lg:block">
          <p className="truncate px-3 text-xs text-ink-foreground/50" data-testid="beheer-admin-email">{adminEmail}</p>
          <Button variant="ghost" size="sm" className="mt-1 w-full justify-start text-ink-foreground/70 hover:bg-white/10 hover:text-ink-foreground" onClick={uitloggen} data-testid="beheer-logout"><LogOut className="mr-2 h-4 w-4" />Uitloggen</Button>
        </div>
      </aside>
      <div className="min-w-0">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b bg-background/85 px-5 backdrop-blur sm:px-8">
          <h1 className="font-display text-lg font-bold tracking-tight">{actief?.label}</h1>
          <Button variant="ghost" size="sm" className="lg:hidden" onClick={uitloggen} data-testid="beheer-logout-mobile"><LogOut className="mr-2 h-4 w-4" />Uitloggen</Button>
        </header>
        <main className="p-5 sm:p-8">
          {section === "pages" && <Pages />}
          {section === "menus" && <Menus />}
          {section === "site" && <SiteSettings />}
          {section === "instellingen" && <Instellingen />}
          {section === "betalingen" && <Betalingen />}
          {section === "productpagina" && <Productpagina TextField={TextField} />}
          {section === "integratie" && <Integratie />}
          {section === "claude" && <Claude />}
          {section === "account" && <Account email={adminEmail} />}
        </main>
      </div>
    </div>
  );
};

export default Beheer;
